import { Chip } from "@modernatx/ui-kit-react";
import { parseISO } from "date-fns";
import React from "react";
import { v4 as uuidv4 } from "uuid";

import { CreditAddress, CreditStatus } from "@/types/Credit";
import { InvoiceAddress, InvoiceStatus, PaymentStatus } from "@/types/Invoice";
import { CreditCardBrand, CreditCardData } from "@/types/PaymentMethod";

import {
  AmexCardIcon,
  BankIcon,
  DiscoverCardIcon,
  GenericCardIcon,
  MastercardIcon,
  VisaCardIcon
} from "../assets/logos/cardLogo";

export const getPaymentStatus = (status: PaymentStatus) => {
  switch (status) {
    case PaymentStatus.Paid:
      return <Chip variant="success" label={status} />;
    case PaymentStatus.Open:
      return <Chip variant="info" label={status} />;
    case PaymentStatus.Overdue:
      return <Chip variant="critical" label={status} />;
    case PaymentStatus.DueSoon:
      return <Chip variant="warning" label={status} />;
    default:
      return <Chip label={status} />;
  }
};

export const formatInvoiceStatus = (status: InvoiceStatus): PaymentStatus => {
  switch (status) {
    case InvoiceStatus.Paid:
      return PaymentStatus.Paid;
    case InvoiceStatus.Open:
      return PaymentStatus.Open;
    case InvoiceStatus.Overdue:
      return PaymentStatus.Overdue;
    case InvoiceStatus.DueSoon:
      return PaymentStatus.DueSoon;
    case InvoiceStatus.Pending:
      return PaymentStatus.Pending;
    default:
      return PaymentStatus.Open;
  }
};

export const formatCreditStatus = (status: CreditStatus) => {
  switch (status) {
    case CreditStatus.Unused:
      return <Chip variant="info" label={"Unused"} />;
    case CreditStatus.Pending:
      return <Chip label={"Pending"} />;
    case CreditStatus.Used:
      return <Chip label={"Used"} />;
    default:
      return "--";
  }
};

export const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}/${day}/${year}`;
};

export const formatAddress = (address?: InvoiceAddress | CreditAddress) => {
  if (!address) return "--";
  const { name, line1, line2, city, state, postalCode } = address;
  return `${name}, ${line1}${line2 ? `, ${line2}` : ""}, ${city}, ${state} ${postalCode}`;
};

export const formatTimestampDate = (date?: string) => {
  if (!date) return "--";
  // to parse the high precision ISO string we get from the backend
  const normalizeToMilliseconds = (input: string) => {
    return input.replace(/\.(\d{3})\d*Z$/, ".$1Z");
  };
  const normalizedDate = normalizeToMilliseconds(date);
  const dueDate = parseISO(normalizedDate);

  return dueDate.toLocaleDateString("en-us", { timeZone: "UTC" });
};

export const formatPrice = (price?: number | string): string => {
  if (price === undefined || price === null) return "";
  const num = typeof price === "number" ? price : parseFloat(price);
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const formatCurrency = (
  value: number | undefined,
  currency: string = "USD"
): string => {
  if (value === undefined) return "--";

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(value / 100);
};

export const attachCurrencySymbolToBalanceString = (
  value: string,
  currency: string
): string => {
  if (value === undefined || value === "") return "";

  const numberValue = parseFloat(value);

  if (isNaN(numberValue)) return value; // fallback if value is not a number

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
  }).format(numberValue);
};

const getCardDetails = (brand?: CreditCardBrand): CreditCardData => {
  const CREDIT_CARD_MAPPING: Record<CreditCardBrand, CreditCardData> = {
    mastercard: { name: "Mastercard", logo: <MastercardIcon /> },
    amex: { name: "American Express", logo: <AmexCardIcon /> },
    visa: { name: "Visa", logo: <VisaCardIcon /> },
    discover: { name: "Discover", logo: <DiscoverCardIcon /> },
    diners: { name: "Diners", logo: <GenericCardIcon /> },
    jcb: { name: "JCB", logo: <GenericCardIcon /> },
    unionpay: { name: "UnionPay", logo: <GenericCardIcon /> },
    elo: { name: "Elo", logo: <GenericCardIcon /> }
  };

  return (
    (brand && CREDIT_CARD_MAPPING[brand]) || {
      name: "Credit Card",
      logo: <GenericCardIcon />
    }
  );
};

export const getCardName = (brand?: CreditCardBrand): string => {
  const CARD_NAME_MAPPING: Record<CreditCardBrand, string> = {
    mastercard: "Mastercard",
    amex: "American Express",
    visa: "Visa",
    discover: "Discover",
    diners: "Diners",
    jcb: "JCB",
    unionpay: "UnionPay",
    elo: "Elo"
  };

  return brand && CARD_NAME_MAPPING[brand]
    ? CARD_NAME_MAPPING[brand]
    : "Credit Card";
};

export const getPaymentDetails = (
  brand?: CreditCardBrand,
  bankName?: string
): CreditCardData => {
  if (bankName) {
    return { name: bankName, logo: <BankIcon /> };
  }
  return getCardDetails(brand);
};

export const generateIdempotencyToken = () => uuidv4();

export const PAYMENT_METHOD_STATUS = {
  ACTIVE: "ACTIVE",
  PENDING: "PENDING"
};

export const PAYMENT_METHOD_TYPES = {
  ACH: "us_bank_account",
  CARD: "card"
};

export const PAYMENT_PROVIDER = "STRIPE";

export const getTodaysDate = (): string => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const year = today.getFullYear();
  return `${month}-${day}-${year}`;
};
